const TOKEN_KEY = 'mydoctorai_token';
const USER_KEY = 'mydoctorai_user';

// Non-credential flag cookie. Used by the landing page (a sibling subdomain)
// to detect that the user is signed in to the portal. The actual JWT lives
// only in this origin's localStorage; this cookie just carries `1`.
//
// Set REACT_APP_COOKIE_DOMAIN in .env to the shared parent domain
// (e.g. `.momentsview.tech`) so the cookie is visible to other subdomains.
// Leave unset in local dev — the cookie will then be scoped to the current
// host only, which is fine since localhost doesn't share subdomains.
const FLAG_COOKIE = 'mdai_authed';
const NAME_COOKIE = 'mdai_name';
const ROLE_COOKIE = 'mdai_role';
const COOKIE_DOMAIN = process.env.REACT_APP_COOKIE_DOMAIN || '';
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days

function writeCookie(name, value, { maxAge = COOKIE_MAX_AGE } = {}) {
  if (typeof document === 'undefined') return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
  ];
  if (COOKIE_DOMAIN) parts.push(`Domain=${COOKIE_DOMAIN}`);
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
}

function eraseCookie(name) {
  writeCookie(name, '', { maxAge: 0 });
}

function setFlagCookie() {
  writeCookie(FLAG_COOKIE, '1');
}

function clearFlagCookie() {
  eraseCookie(FLAG_COOKIE);
}

// Shares a tiny snapshot of the user with sibling subdomains (e.g. landing
// page) so they can render "Hi, Aisha" + dropdown without making an auth
// API call. Only first name + role — never email or phone.
function setUserCookies(user) {
  if (!user) return;
  const firstName = (user.name || '').trim().split(/\s+/)[0] || '';
  writeCookie(NAME_COOKIE, firstName);
  writeCookie(ROLE_COOKIE, user.role || '');
}

function clearUserCookies() {
  eraseCookie(NAME_COOKIE);
  eraseCookie(ROLE_COOKIE);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    setFlagCookie();
  }
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredUser(user) {
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setUserCookies(user);
  }
}

export function isAuthenticated() {
  return Boolean(getToken());
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  clearFlagCookie();
  clearUserCookies();
}

export function logout() {
  clearAuth();
  window.location.href = '/login';
}
